// ─── How it works — Sơ đồ luồng Copy Trade & Trailing Stop ───
// Hai diagram dạng lớp (layered) dựng bằng div + CSS, tái dùng
// LayerNode/Connector từ ArchitectureDiagram để giữ cùng style.

import { LayerNode, Connector } from './ArchitectureDiagram';

export function CopyTradeFlowDiagram() {
  return (
    <div className="bg-background-100 rounded-xl border border-background-200/70 p-5 md:p-6">
      <div className="flex items-center gap-2 mb-5">
        <i className="ri-swap-line text-emerald-400 text-base w-5 h-5 flex items-center justify-center"></i>
        <p className="text-sm font-heading font-semibold text-foreground-900">Sơ đồ luồng Copy Trade — cTrader → MT4</p>
      </div>

      <div className="max-w-2xl mx-auto">
        {/* Layer 1: nguồn cTrader */}
        <LayerNode
          icon="ri-bank-line"
          tone="secondary"
          title="Tài khoản cTrader (nguồn)"
          subtitle="Danh sách lệnh đang mở trên tài khoản nguồn. Đây là nguồn sự thật duy nhất — không dùng hàng đợi tín hiệu."
          chips={['open positions', 'volume', 'SL/TP']}
        />

        {/* Trigger: hai ngõ vào */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-1.5">
          <div className="flex flex-col items-center">
            <span className="w-px h-3 bg-background-300/70"></span>
            <span className="px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-400 text-[10px] font-mono">
              execution event (~100ms)
            </span>
            <div className="w-6 h-6 rounded-full bg-background-100 border border-background-300/70 flex items-center justify-center my-0.5">
              <i className="ri-arrow-down-line text-foreground-400 text-xs w-4 h-4 flex items-center justify-center"></i>
            </div>
          </div>
          <div className="flex flex-col items-center">
            <span className="w-px h-3 bg-background-300/70"></span>
            <span className="px-2 py-0.5 rounded bg-secondary-500/10 text-secondary-400 text-[10px] font-mono">
              reconcile định kỳ (30 giây)
            </span>
            <div className="w-6 h-6 rounded-full bg-background-100 border border-background-300/70 flex items-center justify-center my-0.5">
              <i className="ri-arrow-down-line text-foreground-400 text-xs w-4 h-4 flex items-center justify-center"></i>
            </div>
          </div>
        </div>

        {/* Layer 2: engine */}
        <LayerNode
          icon="ri-swap-line"
          tone="emerald"
          title="Copy Trade Engine"
          subtitle="Gọi get-positions qua gateway, chuẩn hoá volume/giá/chiều, gom thành một snapshot 'desired state' cho từng cặp copy."
          chips={['reconcile', 'desired-state', 'normalise']}
        />

        <Connector label="upsert copy_trade_state (Supabase)" />

        {/* Layer 3: state table */}
        <LayerNode
          icon="ri-database-2-line"
          tone="primary"
          title="copy_trade_state — desired state"
          subtitle="Bảng lưu snapshot lệnh mong muốn cho mỗi cặp (source → target). MT4 chỉ cần đọc bảng này để biết phải khớp cái gì."
          chips={['position_snapshot', 'is_active', 'last_reconciled_at']}
        />

        <Connector label="MT4 EA đọc & khớp (poll)" />

        {/* Layer 4: MT4 */}
        <LayerNode
          icon="ri-terminal-box-line"
          tone="accent"
          title="EA trên MT4 (target)"
          subtitle="Đọc desired state rồi tự cân bằng lệnh của mình cho khớp: mở lệnh thiếu, đóng lệnh thừa, sửa SL/TP cho đúng."
          chips={['open missing', 'close extra', 'fix SL/TP']}
        />

        {/* Loop-back note */}
        <div className="mt-4 flex items-center justify-center gap-2 text-foreground-500 text-[11px]">
          <i className="ri-loop-left-line text-emerald-400 w-4 h-4 flex items-center justify-center"></i>
          <span>
            Khi tắt engine, cặp bị đánh dấu <strong className="text-foreground-700">is_active=false</strong> và snapshot rỗng → EA MT4 <strong className="text-foreground-700">đóng băng</strong>, không mở/đóng gì thêm.
          </span>
        </div>
      </div>
    </div>
  );
}

export function TrailingStopFlowDiagram() {
  return (
    <div className="bg-background-100 rounded-xl border border-background-200/70 p-5 md:p-6">
      <div className="flex items-center gap-2 mb-5">
        <i className="ri-shield-check-line text-primary-400 text-base w-5 h-5 flex items-center justify-center"></i>
        <p className="text-sm font-heading font-semibold text-foreground-900">Sơ đồ luồng Trailing Stop</p>
      </div>

      <div className="max-w-2xl mx-auto">
        {/* Layer 1: vị thế cTrader */}
        <LayerNode
          icon="ri-bank-line"
          tone="secondary"
          title="Vị thế đang mở trên cTrader"
          subtitle="Engine gọi get-account-data để lấy trader + reconcile + symbol, chỉ theo dõi các cặp Forex (bỏ qua vàng/bạc/dầu/chỉ số/crypto)."
          chips={['get-account-data', 'Forex only']}
        />

        {/* Split: price stream + refresh */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-1.5">
          <div className="flex flex-col items-center">
            <span className="w-px h-3 bg-background-300/70"></span>
            <span className="px-2 py-0.5 rounded bg-primary-500/10 text-primary-400 text-[10px] font-mono">
              spot tick (realtime)
            </span>
            <div className="w-6 h-6 rounded-full bg-background-100 border border-background-300/70 flex items-center justify-center my-0.5">
              <i className="ri-arrow-down-line text-foreground-400 text-xs w-4 h-4 flex items-center justify-center"></i>
            </div>
          </div>
          <div className="flex flex-col items-center">
            <span className="w-px h-3 bg-background-300/70"></span>
            <span className="px-2 py-0.5 rounded bg-secondary-500/10 text-secondary-400 text-[10px] font-mono">
              refresh vị thế (60 giây)
            </span>
            <div className="w-6 h-6 rounded-full bg-background-100 border border-background-300/70 flex items-center justify-center my-0.5">
              <i className="ri-arrow-down-line text-foreground-400 text-xs w-4 h-4 flex items-center justify-center"></i>
            </div>
          </div>
        </div>

        {/* Layer 2: engine */}
        <LayerNode
          icon="ri-shield-check-line"
          tone="primary"
          title="Trailing Stop Engine"
          subtitle="Nhận tick qua price bus, tính profit theo pip, rồi chạy 2 bước: STEP 1 set SL/TP ban đầu, STEP 2 dời SL theo lợi nhuận."
          chips={['calc profit pips', 'STEP 1 + STEP 2', 'ratchet SL']}
        />

        <Connector label="amend-sltp / close-position" />

        {/* Layer 3: gateway + cTrader */}
        <LayerNode
          icon="ri-loop-left-line"
          tone="accent"
          title="Gửi lệnh sửa ngược về cTrader"
          subtitle="STEP 1: set SL/TP cho lệnh chưa có. STEP 2: dời SL lên (BUY) hoặc xuống (SELL). Khi chạm TP, tự động đóng lệnh."
          chips={['amend-sltp', 'close-position']}
        />

        {/* Branch: 3 modes + TP hit */}
        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-2">
          {[
            { icon: 'ri-settings-2-line', label: 'Setup Only', desc: 'chỉ set SL/TP ban đầu' },
            { icon: 'ri-shield-star-line', label: 'Simple', desc: 'dời SL về hoà vốn (+1 pip)' },
            { icon: 'ri-shield-flash-line', label: 'Full', desc: 'hoà vốn + trailing từng step' },
          ].map((m) => (
            <div key={m.label} className="flex items-start gap-2 px-3 py-2.5 rounded-lg bg-background-50 border border-primary-500/15">
              <i className={`${m.icon} text-primary-400 text-sm w-4 h-4 flex items-center justify-center shrink-0 mt-0.5`}></i>
              <div className="min-w-0">
                <p className="text-foreground-900 text-xs font-semibold">{m.label}</p>
                <p className="text-foreground-600 text-[10px] leading-relaxed mt-0.5">{m.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}